import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { PublicFooter } from "@/components/PublicFooter";
import { useAuth } from "@/hooks/useAuth";
import {
  useProfile,
  useIsAdmin,
  useBillingStatus,
  useSubscriptions,
  useSalary,
} from "@/lib/queries";
import { toCsv, downloadCsv, todayIso } from "@/lib/csv";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { setLanguage, type Lang } from "@/i18n";
import { Shield, Trash2, Download } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { deleteMyAccount } from "@/lib/account.functions";
import { scheduleDowngrade, undoDowngrade, createPortalSession } from "@/lib/billing.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { COUNTRIES } from "@/lib/countries";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { z } from "zod";
import {
  OCCUPATION_OPTIONS,
  OCCUPATION_VALUES,
  isOccupation,
  type Occupation,
} from "@/lib/occupation";

export const Route = createFileRoute("/_authenticated/profile")({
  head: () => ({ meta: [{ title: "Profile — VISIKT" }] }),
  component: ProfilePage,
});

const phoneRegex = /^[+\d][\d\s\-()]{6,}$/;

const profileSchema = z.object({
  display_name: z.string().trim().max(100).optional(),
  first_name: z.string().trim().min(1).max(80),
  last_name: z.string().trim().min(1).max(80),
  postal_code: z.string().trim().min(1).max(16),
  city: z.string().trim().min(1).max(80),
  country: z.string().trim().min(1).max(80),
  phone: z.string().trim().regex(phoneRegex),
  occupation: z.enum(OCCUPATION_VALUES),
});

type ProfileForm = Omit<z.infer<typeof profileSchema>, "occupation"> & {
  occupation: Occupation | "";
};

function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const profile = useProfile();
  const isAdmin = useIsAdmin();
  const billing = useBillingStatus();
  const subscriptionsQuery = useSubscriptions();
  const salaryQuery = useSalary();

  function handleExport() {
    if (!billing.data?.isActive) {
      toast.error(t("profile.exportProOnly"));
      return;
    }
    const subs = subscriptionsQuery.data ?? [];
    const salary = salaryQuery.data ?? [];
    if (subs.length === 0 && salary.length === 0) return;
    const date = todayIso();
    if (salary.length > 0) {
      const csv = toCsv(
        [
          "effective_date",
          "gross_annual_nok",
          "bonus_nok",
          "vacation_pay_nok",
          "benefits_nok",
          "other_income_nok",
          "notes",
          "created_at",
        ],
        salary.map((s) => {
          const x = s as typeof s & {
            vacation_pay_nok?: number | null;
            benefits_nok?: number | null;
            other_income_nok?: number | null;
          };
          return [
            s.effective_date,
            s.gross_annual_nok,
            s.bonus_nok,
            x.vacation_pay_nok ?? null,
            x.benefits_nok ?? null,
            x.other_income_nok ?? null,
            s.notes,
            (s as unknown as { created_at?: string }).created_at ?? null,
          ];
        }),
      );
      downloadCsv(`visikt-salary-${date}.csv`, csv);
    }
    if (subs.length > 0) {
      const csv = toCsv(
        [
          "name",
          "category",
          "monthly_cost_nok",
          "billing_cycle",
          "start_date",
          "is_active",
          "notes",
          "created_at",
        ],
        subs.map((s) => [
          s.name,
          s.category,
          s.monthly_cost_nok,
          s.billing_cycle,
          s.start_date,
          s.is_active,
          s.notes,
          (s as unknown as { created_at?: string }).created_at ?? null,
        ]),
      );
      downloadCsv(`visikt-subscriptions-${date}.csv`, csv);
    }
    toast.success(t("profile.exportReady"));
  }
  const qc = useQueryClient();
  const navigate = useNavigate();
  const currentLang = (i18n.language === "nb" ? "nb" : "en") as Lang;
  const deleteAccountFn = useServerFn(deleteMyAccount);
  const scheduleDowngradeFn = useServerFn(scheduleDowngrade);
  const undoDowngradeFn = useServerFn(undoDowngrade);
  const portalFn = useServerFn(createPortalSession);

  const downgradeMutation = useMutation({
    mutationFn: async () => {
      const res = await scheduleDowngradeFn({ data: { environment: getStripeEnvironment() } });
      if ("error" in res) throw new Error(res.error);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["billing-status"] });
      toast.success(t("profile.downgradeScheduledToast"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const undoMutation = useMutation({
    mutationFn: async () => {
      const res = await undoDowngradeFn({ data: { environment: getStripeEnvironment() } });
      if ("error" in res) throw new Error(res.error);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["billing-status"] });
      toast.success(t("profile.undoDowngradeToast"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const portalMutation = useMutation({
    mutationFn: async () => {
      const res = await portalFn({
        data: {
          environment: getStripeEnvironment(),
          returnUrl: `${window.location.origin}/profile`,
        },
      });
      if ("error" in res) throw new Error(res.error);
      return res.url;
    },
    onSuccess: (url) => {
      window.open(url, "_blank", "noopener,noreferrer");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [form, setForm] = useState<ProfileForm>({
    display_name: "",
    first_name: "",
    last_name: "",
    postal_code: "",
    city: "",
    country: "Norway",
    phone: "",
    occupation: "",
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const p = profile.data as Record<string, string | null> | null | undefined;
    if (!p) return;
    setForm({
      display_name: p.display_name ?? "",
      first_name: p.first_name ?? "",
      last_name: p.last_name ?? "",
      postal_code: p.postal_code ?? "",
      city: p.city ?? "",
      country: p.country ?? "Norway",
      phone: p.phone ?? "",
      occupation: isOccupation(p.occupation) ? p.occupation : "",
    });
  }, [profile.data]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    const parsed = profileSchema.safeParse(form);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const iss of parsed.error.issues) {
        const key = String(iss.path[0] ?? "");
        if (key) next[key] = iss.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    setSaving(true);
    const v = parsed.data;
    const { error } = await supabase
      .from("profiles")
      .update({
        display_name: v.display_name?.trim() || `${v.first_name} ${v.last_name}`.trim(),
        first_name: v.first_name,
        last_name: v.last_name,
        postal_code: v.postal_code,
        city: v.city,
        country: v.country,
        phone: v.phone,
        occupation: v.occupation,
      })
      .eq("id", user.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("profile.updated"));
    qc.invalidateQueries({ queryKey: ["profile"] });
  }

  async function changeLang(next: Lang) {
    setLanguage(next);
    if (user) {
      await supabase.from("profiles").update({ locale: next }).eq("id", user.id);
      qc.invalidateQueries({ queryKey: ["profile"] });
    }
  }

  async function handleSignOut() {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  const deleteMutation = useMutation({
    mutationFn: async () => deleteAccountFn({ data: { environment: getStripeEnvironment() } }),
    onSuccess: async () => {
      toast.success(t("profile.deleted"));
      await qc.cancelQueries();
      qc.clear();
      await supabase.auth.signOut();
      navigate({ to: "/", replace: true });
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : t("profile.deleteFailed");
      toast.error(msg);
    },
  });

  const isPro = billing.data?.isActive;
  const periodEnd = billing.data?.currentPeriodEnd
    ? new Date(billing.data.currentPeriodEnd).toLocaleDateString(
        currentLang === "nb" ? "nb-NO" : "en-GB",
      )
    : null;

  return (
    <AppShell>
      <section className="animate-entry">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
          {t("profile.eyebrow")}
        </p>
        <h1 className="text-2xl font-display font-bold tracking-tight mb-6">
          {t("profile.title")}
        </h1>

        <form onSubmit={handleSave} className="space-y-4 border-y border-border py-6">
          <Field label={t("profile.email")}>
            <input className="input-base" type="email" disabled value={user?.email ?? ""} />
          </Field>
          <Field label={t("profile.name")}>
            <input
              className="input-base"
              type="text"
              maxLength={100}
              value={form.display_name ?? ""}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder={t("profile.namePlaceholder")}
            />
          </Field>

          <div className="pt-2 space-y-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {t("profile.personal")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("profile.firstName")} error={errors.first_name}>
                <input
                  className="input-base"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  autoComplete="given-name"
                />
              </Field>
              <Field label={t("profile.lastName")} error={errors.last_name}>
                <input
                  className="input-base"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  autoComplete="family-name"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("profile.postalCode")} error={errors.postal_code}>
                <input
                  className="input-base"
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                />
              </Field>
              <Field label={t("profile.city")} error={errors.city}>
                <input
                  className="input-base"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </Field>
            </div>
            <Field label={t("profile.country")} error={errors.country}>
              <select
                className="input-base"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
              >
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("profile.phone")} error={errors.phone}>
              <input
                className="input-base"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+47 900 00 000"
              />
            </Field>
            <Field label={t("occupation.label")} error={errors.occupation}>
              <select
                className="input-base"
                value={form.occupation}
                onChange={(e) =>
                  setForm({ ...form, occupation: e.target.value as Occupation | "" })
                }
              >
                <option value="" disabled>
                  {t("occupation.placeholder")}
                </option>
                {OCCUPATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 rounded-md bg-accent text-primary font-medium text-sm disabled:opacity-50"
          >
            {saving ? t("common.saving") : t("common.save")}
          </button>
        </form>
      </section>

      <section>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
          {t("profile.plan")}
        </p>
        <div className="border border-border rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{t("profile.currentPlan")}</span>
            <span className="text-sm font-semibold">{billing.data?.planLabel ?? "Free"}</span>
          </div>

          {isPro && periodEnd && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("profile.nextBilling")}</span>
              <span className="text-sm font-mono">{periodEnd}</span>
            </div>
          )}

          {isPro && billing.data?.cancelAtPeriodEnd && periodEnd && (
            <p className="text-[12px] text-muted-foreground">
              {t("profile.downgradeScheduled", { date: periodEnd })}
            </p>
          )}

          {isPro && billing.data?.status === "past_due" && (
            <p className="text-[12px] rounded-md border border-destructive/40 bg-destructive/5 text-destructive px-3 py-2">
              {t("profile.pastDue")}
            </p>
          )}

          {!isPro && (
            <Link
              to="/plans"
              className="block w-full text-center py-3 rounded-md bg-foreground text-background font-semibold text-sm"
            >
              {t("profile.upgrade")}
            </Link>
          )}

          {isPro && (
            <button
              type="button"
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
              className="w-full py-3 rounded-md border border-border text-sm hover:bg-foreground/5 transition-colors disabled:opacity-50"
            >
              {portalMutation.isPending ? t("common.saving") : t("profile.manageBilling")}
            </button>
          )}

          {isPro && !billing.data?.cancelAtPeriodEnd && (
            <button
              type="button"
              onClick={() => downgradeMutation.mutate()}
              disabled={downgradeMutation.isPending}
              className="w-full py-3 rounded-md border border-border text-sm hover:bg-foreground/5 transition-colors disabled:opacity-50"
            >
              {downgradeMutation.isPending ? t("common.saving") : t("profile.downgrade")}
            </button>
          )}
          {isPro && billing.data?.cancelAtPeriodEnd && (
            <button
              type="button"
              onClick={() => undoMutation.mutate()}
              disabled={undoMutation.isPending}
              className="w-full py-3 rounded-md bg-foreground text-background font-semibold text-sm hover:bg-foreground/90 transition-colors disabled:opacity-50"
            >
              {undoMutation.isPending ? t("common.saving") : t("profile.undoDowngrade")}
            </button>
          )}
        </div>
      </section>

      <section>
        <h2 className="font-display font-bold text-sm tracking-tight mb-4">
          {t("profile.account")}
        </h2>
        <div className="space-y-3">
          <div className="flex justify-between text-sm py-2 border-b border-border">
            <span className="text-muted-foreground">{t("profile.currency")}</span>
            <span className="font-mono">NOK</span>
          </div>
          <div className="flex items-center justify-between text-sm py-2 border-b border-border">
            <span className="text-muted-foreground">{t("common.language")}</span>
            <div className="flex items-center text-[10px] font-mono uppercase tracking-widest border border-border rounded-full overflow-hidden">
              <button
                type="button"
                onClick={() => changeLang("en")}
                className={
                  "px-3 py-1.5 transition-colors " +
                  (currentLang === "en" ? "bg-foreground text-background" : "text-muted-foreground")
                }
              >
                {t("common.english")}
              </button>
              <button
                type="button"
                onClick={() => changeLang("nb")}
                className={
                  "px-3 py-1.5 transition-colors " +
                  (currentLang === "nb" ? "bg-foreground text-background" : "text-muted-foreground")
                }
              >
                {t("common.norwegian")}
              </button>
            </div>
          </div>
        </div>
        {isAdmin.data && (
          <Link
            to="/admin"
            className="mt-6 w-full py-3 rounded-md border border-border text-sm flex items-center justify-center gap-2 hover:bg-muted/40 transition-colors"
          >
            <Shield className="size-4" /> Admin panel
          </Link>
        )}
        <button
          onClick={handleSignOut}
          className="mt-3 w-full py-3 rounded-md border border-border text-sm text-[color:var(--color-negative)]"
        >
          {t("common.signOut")}
        </button>
      </section>

      <section>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
          {t("profile.dataSection")}
        </p>
        {(() => {
          const loading = subscriptionsQuery.isLoading || salaryQuery.isLoading;
          const empty =
            !loading &&
            (subscriptionsQuery.data?.length ?? 0) === 0 &&
            (salaryQuery.data?.length ?? 0) === 0;
          return (
            <>
              <button
                type="button"
                onClick={handleExport}
                disabled={loading || empty || !isPro}
                className="w-full py-3 rounded-md border border-border text-sm flex items-center justify-center gap-2 hover:bg-foreground/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="size-4" /> {t("profile.exportCsv")}
              </button>
              {!isPro ? (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  {t("profile.exportProOnly")}{" "}
                  <Link to="/plans" className="underline hover:no-underline">
                    {t("profile.upgrade")}
                  </Link>
                </p>
              ) : empty ? (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  {t("profile.nothingToExport")}
                </p>
              ) : null}
            </>
          );
        })()}
      </section>

      <section>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
          {t("profile.legal.title")}
        </p>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-foreground underline underline-offset-4 hover:text-accent transition-colors"
          >
            {t("profile.legal.terms")}
          </a>
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-foreground underline underline-offset-4 hover:text-accent transition-colors"
          >
            {t("profile.legal.privacy")}
          </a>
        </div>
      </section>

      <section>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
          {t("profile.support")}
        </p>
        <Link
          to="/contact"
          className="w-full py-3 rounded-md border border-border text-sm flex items-center justify-center gap-2 hover:bg-foreground/5 transition-colors"
        >
          {t("profile.contactSupport")}
        </Link>
      </section>

      <section>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
          {t("profile.danger")}
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              className="w-full py-3 rounded-md border border-destructive/50 text-destructive text-sm font-medium flex items-center justify-center gap-2 hover:bg-destructive/5 transition-colors"
            >
              <Trash2 className="size-4" /> {t("profile.deleteAccount")}
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("profile.deleteConfirmTitle")}</AlertDialogTitle>
              <AlertDialogDescription>{t("profile.deleteConfirmBody")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                disabled={deleteMutation.isPending}
                onClick={(e) => {
                  e.preventDefault();
                  deleteMutation.mutate();
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending
                  ? t("profile.deleting")
                  : t("profile.deleteConfirmAction")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>

      <PublicFooter />
    </AppShell>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
        {label}
      </span>
      {children}
      {error && <span className="block text-[11px] text-destructive mt-1">{error}</span>}
    </label>
  );
}
